import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Mic, Send, Square, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AudioPlayer } from './AudioPlayer';

// Gravador de áudio do atendente. Ocupa a linha do composer enquanto está ativo:
// grava → revisa → envia (ou descarta).
//
// Formato: o WhatsApp Cloud API não aceita webm, que é o que o Chromium grava
// por padrão. Pedimos mp4/ogg quando o navegador suporta (Safari dá mp4, Firefox
// dá ogg) e, no pior caso, mandamos webm mesmo — a API converte com ffmpeg.

// Ordem de preferência: os dois primeiros o WhatsApp aceita sem conversão.
const FORMATOS = ['audio/mp4', 'audio/ogg;codecs=opus', 'audio/webm;codecs=opus', 'audio/webm'];

function escolherFormato(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  return FORMATOS.find((f) => MediaRecorder.isTypeSupported(f));
}

function extensaoDe(mime: string) {
  if (mime.includes('mp4')) return 'm4a';
  if (mime.includes('ogg')) return 'ogg';
  return 'webm';
}

function formatTime(s: number) {
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
}

const BARRAS = 28;

interface AudioRecorderProps {
  onCancel: () => void;
  onSend: (blob: Blob, filename: string) => void;
  enviando: boolean;
}

const LIMITE_SEGUNDOS = 10 * 60;

export function AudioRecorder({ onCancel, onSend, enviando }: AudioRecorderProps) {
  const [estado, setEstado] = useState<'iniciando' | 'gravando' | 'revisando' | 'erro'>(
    'iniciando'
  );
  const [erro, setErro] = useState('');
  const [segundos, setSegundos] = useState(0);
  const [niveis, setNiveis] = useState<number[]>(() => Array(BARRAS).fill(0.06));
  const [gravado, setGravado] = useState<{ blob: Blob; url: string; nome: string } | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);
  const pedacosRef = useRef<Blob[]>([]);
  // Cancelamento explícito: no onstop precisamos saber se o áudio deve virar
  // preview ou ser jogado fora.
  const descartarRef = useRef(false);

  // Solta microfone, medidor e timers. Idempotente — é chamado no descarte, no
  // stop e no unmount.
  const encerrarCaptura = useCallback(() => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    audioCtxRef.current?.close().catch(() => {});
    audioCtxRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    let vivo = true;

    const iniciar = async () => {
      const mimeType = escolherFormato();
      if (!navigator.mediaDevices?.getUserMedia || !mimeType) {
        setErro('Seu navegador não suporta gravação de áudio.');
        setEstado('erro');
        return;
      }

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
      } catch {
        setErro('Não foi possível acessar o microfone. Verifique a permissão do navegador.');
        setEstado('erro');
        return;
      }

      // O componente pode ter sido desmontado durante o prompt de permissão.
      if (!vivo) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      streamRef.current = stream;
      pedacosRef.current = [];

      const rec = new MediaRecorder(stream, { mimeType });
      recorderRef.current = rec;
      rec.ondataavailable = (e) => {
        if (e.data.size) pedacosRef.current.push(e.data);
      };
      rec.onstop = () => {
        encerrarCaptura();
        if (descartarRef.current) return;
        const blob = new Blob(pedacosRef.current, { type: mimeType });
        if (!blob.size) {
          setErro('A gravação saiu vazia. Tente novamente.');
          setEstado('erro');
          return;
        }
        setGravado({
          blob,
          url: URL.createObjectURL(blob),
          nome: `audio.${extensaoDe(mimeType)}`,
        });
        setEstado('revisando');
      };
      rec.start();
      setEstado('gravando');

      // Medidor de volume: alimenta a onda que anda da direita pra esquerda,
      // dando o retorno visual de que o microfone está captando de verdade.
      const ctx = new AudioContext();
      audioCtxRef.current = ctx;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const dados = new Uint8Array(analyser.frequencyBinCount);

      let ultimo = 0;
      const medir = (t: number) => {
        rafRef.current = requestAnimationFrame(medir);
        if (t - ultimo < 80) return; // ~12 barras/s: rápido o bastante, sem custo
        ultimo = t;
        analyser.getByteTimeDomainData(dados);
        let soma = 0;
        for (let i = 0; i < dados.length; i++) {
          const v = (dados[i] - 128) / 128;
          soma += v * v;
        }
        const rms = Math.sqrt(soma / dados.length);
        setNiveis((n) => [...n.slice(1), Math.min(1, Math.max(0.06, rms * 3.2))]);
      };
      rafRef.current = requestAnimationFrame(medir);
    };

    iniciar();
    return () => {
      vivo = false;
      descartarRef.current = true;
      if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
      encerrarCaptura();
    };
  }, [encerrarCaptura]);

  // Cronômetro só enquanto grava.
  useEffect(() => {
    if (estado !== 'gravando') return;
    const id = setInterval(() => setSegundos((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [estado]);

  // Teto de 10 min: passa disso e o arquivo encosta no limite de 16 MB do
  // WhatsApp. Para sozinho e deixa revisar/enviar o que foi gravado.
  useEffect(() => {
    if (
      estado === 'gravando' &&
      segundos >= LIMITE_SEGUNDOS &&
      recorderRef.current?.state === 'recording'
    ) {
      descartarRef.current = false;
      recorderRef.current.stop();
    }
  }, [estado, segundos]);

  // Libera o objectURL do preview ao trocar/desmontar.
  useEffect(() => {
    return () => {
      if (gravado) URL.revokeObjectURL(gravado.url);
    };
  }, [gravado]);

  const parar = () => {
    descartarRef.current = false;
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
  };

  const descartar = () => {
    descartarRef.current = true;
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    encerrarCaptura();
    onCancel();
  };

  if (estado === 'erro') {
    return (
      <div className="flex flex-1 items-center justify-between gap-2 rounded-xl border border-secondary/40 bg-secondary/5 px-3.5 py-2.5">
        <span className="text-xs text-secondary">{erro}</span>
        <button
          type="button"
          onClick={onCancel}
          className="shrink-0 text-xs font-semibold text-text-secondary hover:text-text-title"
        >
          Fechar
        </button>
      </div>
    );
  }

  if (estado === 'revisando' && gravado) {
    return (
      <div className="flex flex-1 items-center gap-2">
        <button
          type="button"
          onClick={descartar}
          disabled={enviando}
          aria-label="Descartar gravação"
          className="flex size-11 shrink-0 items-center justify-center rounded-xl border border-border text-text-secondary transition-colors hover:border-secondary hover:text-secondary disabled:opacity-40"
        >
          <Trash2 size={17} />
        </button>
        <div className="min-w-0 flex-1 rounded-xl border border-border bg-main px-1.5 py-0.5">
          <AudioPlayer url={gravado.url} outgoing={false} />
        </div>
        <button
          type="button"
          onClick={() => onSend(gravado.blob, gravado.nome)}
          disabled={enviando}
          aria-label="Enviar áudio"
          className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary text-white transition-opacity hover:opacity-90 disabled:opacity-40"
        >
          {enviando ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-1 items-center gap-2">
      <button
        type="button"
        onClick={descartar}
        aria-label="Cancelar gravação"
        className="flex size-11 shrink-0 items-center justify-center rounded-xl border border-border text-text-secondary transition-colors hover:border-secondary hover:text-secondary"
      >
        <Trash2 size={17} />
      </button>

      <div className="flex min-w-0 flex-1 items-center gap-3 rounded-xl border border-border bg-main px-3.5 py-2.5">
        <span className="flex shrink-0 items-center gap-1.5 text-xs font-semibold tabular-nums text-secondary">
          <span className="size-2 animate-pulse rounded-full bg-secondary" />
          {estado === 'iniciando' ? 'Ligando…' : formatTime(segundos)}
        </span>
        <div className="flex h-6 flex-1 items-center gap-[2px]">
          {niveis.map((n, i) => (
            <span
              key={i}
              style={{ height: `${Math.round(n * 100)}%` }}
              className="w-full rounded-full bg-primary/70 transition-[height] duration-75"
            />
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={parar}
        disabled={estado !== 'gravando'}
        aria-label="Parar gravação"
        className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary text-white transition-opacity hover:opacity-90 disabled:opacity-40"
      >
        <Square size={16} fill="currentColor" />
      </button>
    </div>
  );
}

// Botão que abre o gravador. Fica ao lado do campo de texto.
export function BotaoGravar({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label="Gravar áudio"
      title="Gravar áudio"
      className={cn(
        'flex size-11 shrink-0 items-center justify-center rounded-xl border border-border text-text-secondary transition-colors',
        'hover:border-primary hover:text-primary disabled:opacity-40'
      )}
    >
      <Mic size={18} />
    </button>
  );
}
